"use client";

import Link from "next/link";
import { memo, useState, useMemo } from "react";

import MemoModal, { MemoInput } from "components/MemoModal/MemoModal";
import { ENUM_APPLICATION_STATUS, ENUM_CUSTOMER_TABS, ENUM_APPLICATION_TYPE } from "constants/customer";
import { applicationStatus } from "constants/status.ts";
import { CustomerApplication } from "graphql/type";
import { cn } from "utils/cn";
import { dateFormat } from "utils/date.util";
import { SupportCarModelNumber, switchSupportCarModelByNumber } from "utils/filter/findLargestCarModel.util";

const ApplicationAbstract = memo(
  ({
    application: {
      id,
      duration,
      spaceGroup: { property },
      applicant,
      status,
      unreadCount,
      updatedAt,
      carUserInfo,
      spaces,
      memo,
      type,
      requestCancelDate,
      approveCancelDate,
    },
    tab,
    updateMemo,
  }: {
    application: CustomerApplication;
    tab: ENUM_CUSTOMER_TABS;
    updateMemo: (id: string, memo: string) => void;
  }) => {
    const [isOpenMemoModal, setIsOpenMemoModal] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleOpenMemoModal = (e: React.MouseEvent<HTMLDivElement>) => {
      // If click memo, open memo modal and prevent Link parent
      e.preventDefault();

      // Open memo modal
      setIsOpenMemoModal(true);
    };

    const handleCloseMemo = () => {
      setIsOpenMemoModal(false);
    };

    const handleUpdateMemo = async ({ memo }: MemoInput) => {
      await updateMemo(id ?? "", memo);

      // Close memo modal
      handleCloseMemo();
    };

    const applicationType = useMemo(() => {
      if (type === ENUM_APPLICATION_TYPE.PARKSTOCK) return "PARKSTOCK";

      return "手動登録";
    }, [type]);

    return (
      <>
        <Link
          href={`/customer/${id}`}
          className="grid min-h-[55px] grid-cols-application items-center border-b border-border bg-white px-4 py-2 text-s transition hover:cursor-pointer hover:bg-grey-light"
        >
          <div className="flex h-full items-center justify-between border-r pr-4">
            <div className="flex min-h-[58px] flex-col justify-center">
              <p className="text-s font-bold text-black">{applicationStatus[status]}</p>
              <p className="text-[10.67px] text-gray">{updatedAt && dateFormat(parseInt(updatedAt))} 更新</p>
            </div>
            {!!unreadCount && (
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-attention text-s font-bold text-white">
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex h-full min-h-[58px] flex-col justify-center border-r px-4">
            <p className="text-s text-black">{applicant.name}</p>
            <p className="text-xs text-gray">{applicant.company?.isCorporate ? "法人契約" : "個人契約"}</p>
          </div>

          <div className="flex h-full flex-col justify-center border-r px-4">
            <p className="text-s text-black">{property?.name}</p>
            <p className="text-xs text-gray">{spaces[0]?.name}</p>
          </div>

          {tab === ENUM_CUSTOMER_TABS.APPLICATION_CONTRACT && (
            <div className="flex h-full min-h-[58px] items-center border-r px-4 text-s">
              {switchSupportCarModelByNumber(carUserInfo[0].carInfo?.type as SupportCarModelNumber, true)}
            </div>
          )}

          <div className="flex h-full min-h-[58px] items-center border-r px-4 text-s">
            {duration?.applyAt && dateFormat(parseInt(duration.applyAt))}
          </div>

          <div className={cn("flex h-full min-h-[58px] items-center border-r px-4 text-s")}>
            {duration?.startAt && dateFormat(duration.startAt)}
          </div>

          {tab === ENUM_CUSTOMER_TABS.UNDER_CONTRACT && (
            <div className={cn("flex h-full min-h-[58px] items-center border-r px-4 text-s")}>
              {status === ENUM_APPLICATION_STATUS.CONTRACT_COMPLETED ? (
                "‐"
              ) : status === ENUM_APPLICATION_STATUS.CANCELLATION_ACCEPTANCE ? (
                <div>
                  <p className="text-xxs text-attention">解約希望日</p>
                  <p className="text-attention">{requestCancelDate && dateFormat(requestCancelDate)}</p>
                </div>
              ) : status === ENUM_APPLICATION_STATUS.CANCELLATION_SCHEDULED ? (
                <>{approveCancelDate && dateFormat(approveCancelDate)}</>
              ) : (
                ""
              )}
            </div>
          )}

          {tab === ENUM_CUSTOMER_TABS.CONTRACT_CANCELLED && (
            <div className={cn("flex h-full min-h-[58px] items-center border-r px-4 text-s")}>
              {approveCancelDate ? dateFormat(approveCancelDate) : "-"}
            </div>
          )}

          <div className={cn("flex h-full min-h-[58px] items-center border-r px-4 text-12px")}>{applicationType}</div>

          {/* Memo */}
          <div className="w-full pl-4" onClick={handleOpenMemoModal}>
            <div className="flex h-[38px] w-[80px] items-center gap-2 bg-white px-4 py-[9px] hover:bg-link-light">
              <img src={`/svg/${memo ? "updateMemo" : "addMemo"}.svg`} alt="IconMemo" />
              <span className="w-[24px] text-12px text-link">メモ</span>
            </div>
          </div>
        </Link>

        <MemoModal isOpen={isOpenMemoModal} onClose={handleCloseMemo} onConfirm={handleUpdateMemo} memo={memo ?? ""} />
      </>
    );
  }
);

ApplicationAbstract.displayName = "ApplicationAbstract";

export default ApplicationAbstract;
